import React, { FC, useState, useCallback, useMemo, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import clsx from 'clsx'

import { makeStyles } from '@mui/styles'
import { createTheme, ThemeProvider, Theme } from '@mui/material/styles'
import { Grid, TextField, InputAdornment, FormHelperText, StyledEngineProvider } from '@mui/material'
import { LocalizationProvider, MobileDatePicker } from '@mui/lab'
import AdapterDateFns from '@mui/lab/AdapterDateFns'

import { icons } from '../../constants/icons'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'
import { Colors } from '../../theme'

const useStyle = makeStyles((theme: Theme) => ({
  main: {
    color: '#fff',
    margin: ({ margin }) => margin,
    marginTop: ({ mt }) => mt,
    '& .MuiOutlinedInput-root': {
      width: ({ width }: IProps) => width || 138,
      height: 40,
      fontSize: '14px',
      borderColor: 'rgba(255,255,255,0.8)',
      cursor: 'pointer',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: ({ error }) => (error ? theme.palette.error.main : 'rgba(255,255,255,0.6)'),
      },
      '&.Mui-error.MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.error.main,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.light,
      },
      '&:active .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.main,
        borderWidth: '1px',
      },
    },
    '& .MuiInputBase-input.MuiOutlinedInput-input': {
      cursor: 'pointer',
    },
    '&+div p': {
      color: Colors.error,
    },
  },
  icon: {
    fontSize: 20,
    opacity: '0.6',
    color: '#A9AAAD',
  },
}))

interface IProps {
  placeholder?: string
  width?: number | string
  margin: number | string
  mt?: number | string
  dataPickerName: string
  required: boolean
  minDate?: string
  maxDate?: string
  disabledToday?: () => boolean
  error?: {
    type: string
    message: string
    ref: HTMLInputElement
  }
  formatValue?: (date: number | Date) => Date
  defaultValue?: string
  showTodayButton?: boolean
}

export const DataPicker: FC<IProps> = ({
  placeholder,
  width,
  mt,
  margin,
  dataPickerName,
  required,
  minDate,
  maxDate,
  formatValue,
  defaultValue,
  showTodayButton,
}) => {
  const disabledToday = useCallback(() => {
    if (minDate) {
      if (new Date(minDate).toISOString().slice(0, 10) > new Date().toISOString().slice(0, 10)) return false
    }
    if (maxDate) {
      if (new Date(maxDate).toISOString().slice(0, 10) < new Date().toISOString().slice(0, 10)) return false
    }
    return true
  }, [minDate, maxDate])

  const theme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#51E5A5',
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            color: '#FFFFFF',
            background: '#424242',
            padding: 10,
            '.MuiDialogContent-root': {
              '& .PrivatePickersToolbar-root': {
                display: 'none',
                // backgroundColor: '#303030',
                // '& h4': {
                //   fontSize: 34,
                //   fontWeight: 400,
                //   color: '#FFFFFF',
                // },
                // '& .MuiButtonBase-root': {
                //   display: 'none',
                // },
              },
              '& .MuiTypography-root': {
                color: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiCalendarPicker-root': {
                '& > div > div': {
                  overflow: 'unset',
                },
                '& .PrivatePickersFadeTransitionGroup-root': {
                  fontWeight: 400,
                },
                '& .MuiSvgIcon-root': {
                  fill: '#FFFFFF',
                },
                '& .PrivatePickersSlideTransition-root': {
                  minHeight: 235,
                  '& .MuiPickersDay-today': {
                    border: 'none',
                    color: '#51E5A5',
                  },
                },
                '& .MuiButtonBase-root': {
                  fontSize: 14,
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                },
                '& .MuiPickersDay-root': {
                  color: '#FFFFFF',
                  backgroundColor: 'transparent',
                  '&.Mui-selected': {
                    color: '#292C32',
                    backgroundColor: '#51E5A5',
                  },
                },
                '&.MuiButtonBase-root-MuiPickersDay-root:not(.Mui-selected)': {
                  color: '#51E5A5',
                  backgroundColor: 'transparent',
                },
              },
            },
            '& .MuiDialogActions-root': {
              justifyContent: 'space-between',
              '& .MuiButton-root': {
                color: '#51E5A5',
                margin: 0,
              },
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            '& :nth-of-type(2)': {
              pointerEvents: disabledToday() ? 'auto' : 'none',
              cursor: disabledToday() ? 'pointer' : 'default',
              color: disabledToday() ? '#51E5A5' : '#888 !important',
            },
          },
        },
      },
    },
  })

  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [nowDate] = useState<Date>(new Date())

  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext()
  const error = errors[dataPickerName]
  const classes = useStyle({ width, placeholder, mt, margin, dataPickerName, required, disabledToday, error })

  const handleDateChange = useCallback((date: Date | string | null) => {
    if (date !== null) {
      // eslint-disable-next-line
      // console.log(`Selected ${date}`)
    }
    if (date === null) {
      // eslint-disable-next-line
      // console.log(`Clear date`)
    }
  }, [])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [setIsOpen])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [setIsOpen])

  const handleDateAccept = useCallback(
    // eslint-disable-next-line
    (date: any) => {
      if (date && formatValue && typeof formatValue === 'function') {
        // eslint-disable-next-line no-param-reassign
        date = formatValue(date)
      }
      setSelectedDate(date)
      setValue(dataPickerName, date, { shouldValidate: true, shouldDirty: true })
      setIsOpen(false)
    },
    [formatValue, setValue, dataPickerName],
  )

  useEffect(() => {
    if (defaultValue) {
      setSelectedDate(new Date(defaultValue))
    }
  }, [dataPickerName, defaultValue])

  const errorMessage = useMemo(() => {
    const message = error?.message.replace(dataPickerName, '')
    return capitalizeFirstLetter(message)
  }, [dataPickerName, error?.message])

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <Grid item className={classes.main}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MobileDatePicker
              {...register(dataPickerName, {
                value: defaultValue,
                required,
              })}
              showTodayButton={showTodayButton}
              clearable
              value={selectedDate}
              open={isOpen}
              onClose={handleClose}
              inputFormat="dd/MM/yyyy"
              minDate={minDate || new Date('01/01/1931')}
              maxDate={maxDate || nowDate}
              onChange={handleDateChange}
              onAccept={handleDateAccept}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <span onClick={handleOpen} className={clsx('material-icons', classes.icon)}>
                      {icons.CALENDAR}
                    </span>
                  </InputAdornment>
                ),
                error: !!error && !selectedDate,
              }}
              renderInput={(params) => <TextField {...params} placeholder={placeholder} onClick={handleOpen} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item>
          {!selectedDate && error && <FormHelperText id="component-error-text">{errorMessage}</FormHelperText>}
        </Grid>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}
